import React, { useState } from 'react';
import { Battery, Zap, Thermometer, Gauge, Activity, Wrench, AlertTriangle, CheckCircle } from 'lucide-react';
import useVehicleStats from '../hooks/useVehicleStats';
import CellHeatmap from './CellHeatmap';
import './DiagnosticPanel.css';

// Circular Progress Ring
const ProgressRing = ({ value, max = 100, size = 120, strokeWidth = 8, color = '#66B4C4' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / max) * circumference;

  return (
    <div className="progress-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          className="progress-ring-circle-bg"
          stroke="rgba(102, 180, 196, 0.1)"
          strokeWidth={strokeWidth}
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="progress-ring-circle"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={offset}
          strokeLinecap="round"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="progress-ring-value">
        <span className="value-number">{Math.round(value)}</span>
        <span className="value-unit">%</span>
      </div>
    </div>
  );
};

// Mini Chart Component
const MiniChart = ({ data, color = '#66B4C4' }) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  return (
    <div className="mini-chart">
      <svg width="100%" height="40" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2"
          points={data.map((value, i) => {
            const x = (i / (data.length - 1)) * 100;
            const y = 40 - ((value - min) / range) * 35;
            return `${x},${y}`;
          }).join(' ')}
        />
      </svg>
    </div>
  );
};

const DiagnosticPanel = ({ data, focusComponent, onComponentFocus }) => {
  const stats = useVehicleStats(data);
  const battery = data?.battery;
  const motor = data?.motor;
  const alerts = data?.alerts || [];
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);

  // Generate dummy chart data
  const tempHistory = Array(20).fill().map((_, i) =>
    28 + Math.sin(i * 0.3) * 2 + Math.random()
  );

  const getStatusColor = (component) => {
    if (focusComponent === component) return 'var(--accent-primary)';
    return 'var(--text-secondary)';
  };

  // Generate smart recommendations based on vehicle state
  const getRecommendations = () => {
    const recommendations = [];

    // Battery-based recommendations
    if (battery?.soc < 20) {
      recommendations.push({
        id: 1,
        severity: 'critical',
        icon: '🔋',
        title: 'Low Battery - Charge Immediately',
        description: 'Battery SOC is below 20%. Charge to at least 80% to prevent deep discharge damage.',
        action: 'Charge Now',
        category: 'Battery'
      });
    } else if (battery?.soc < 40) {
      recommendations.push({
        id: 2,
        severity: 'warning',
        icon: '⚡',
        title: 'Moderate Battery Level',
        description: 'Consider charging soon for optimal range availability.',
        action: 'Schedule Charge',
        category: 'Battery'
      });
    }

    // Temperature-based recommendations
    if (battery?.temperature?.pack > 40) {
      recommendations.push({
        id: 3,
        severity: 'warning',
        icon: '🌡️',
        title: 'Elevated Battery Temperature',
        description: 'Battery temperature is high. Reduce fast charging frequency and allow cooling.',
        action: 'Monitor Temperature',
        category: 'Thermal'
      });
    }

    // Cell imbalance recommendations
    if (battery?.cells?.imbalance > 0.15) {
      recommendations.push({
        id: 4,
        severity: 'warning',
        icon: '⚖️',
        title: 'Cell Imbalance Detected',
        description: `Cell voltage imbalance: ${battery?.cells?.imbalance?.toFixed(3)}V. Perform full charge cycle for balancing.`,
        action: 'Balance Cells',
        category: 'Battery'
      });
    }

    // SOH-based recommendations
    if (battery?.soh < 85) {
      recommendations.push({
        id: 5,
        severity: 'info',
        icon: '🔧',
        title: 'Battery Health Declining',
        description: `SOH at ${battery?.soh}%. Consider battery health assessment at service center.`,
        action: 'Schedule Service',
        category: 'Maintenance'
      });
    }

    // Motor temperature
    if (motor?.temperature > 80) {
      recommendations.push({
        id: 6,
        severity: 'warning',
        icon: '⚙️',
        title: 'Motor Temperature High',
        description: 'Reduce aggressive acceleration. Check cooling system.',
        action: 'Reduce Load',
        category: 'Motor'
      });
    }

    // Active alerts-based recommendations
    if (alerts.length > 0) {
      alerts.forEach((alert, idx) => {
        recommendations.push({
          id: 100 + idx,
          severity: alert.severity || 'info',
          icon: '⚠️',
          title: `DTC: ${alert.id}`,
          description: alert.description || alert.title,
          action: 'View Details',
          category: 'Diagnostic'
        });
      });
    }

    // General maintenance recommendations
    if (data?.vehicle?.odometer > 8000 && data?.vehicle?.odometer % 10000 < 1000) {
      recommendations.push({
        id: 7,
        severity: 'info',
        icon: '🛠️',
        title: 'Scheduled Maintenance Due',
        description: 'Vehicle approaching 10,000 km service interval.',
        action: 'Book Service',
        category: 'Maintenance'
      });
    }

    // If everything is good
    if (recommendations.length === 0) {
      recommendations.push({
        id: 0,
        severity: 'success',
        icon: '✓',
        title: 'All Systems Optimal',
        description: 'No immediate action required. Vehicle operating within normal parameters.',
        action: 'Continue Monitoring',
        category: 'Status'
      });
    }

    return recommendations;
  };

  const recommendations = getRecommendations();

  // Get detailed info for a recommendation
  const getRecommendationDetails = (rec) => {
    const baseDetails = {
      overview: rec.description,
      impacts: [],
      steps: [],
      safetyNotes: [],
      estimatedTime: ''
    };

    // Customize details based on category
    switch (rec.category) {
      case 'Battery':
        if (rec.title.includes('Low Battery')) {
          baseDetails.impacts = [
            'Reduced available range for trips',
            'Risk of deep discharge if left unused',
            'Potential battery degradation if SOC stays below 10%'
          ];
          baseDetails.steps = [
            'Connect to AC charger (3.3kW) for slow charging',
            'Or use DC fast charger (50kW) for quick charge',
            'Charge to at least 80% for optimal battery health',
            'Avoid charging to 100% unless planning long trip'
          ];
          baseDetails.safetyNotes = [
            'Ensure charging port is clean and dry',
            'Check for any error messages on dashboard',
            'Monitor battery temperature during charging'
          ];
          baseDetails.estimatedTime = '4-6 hours (AC) or 45 mins (DC fast charge)';
        } else if (rec.title.includes('Cell Imbalance')) {
          baseDetails.impacts = [
            'Reduced effective battery capacity',
            'Inaccurate SOC readings',
            'Potential for premature cell degradation'
          ];
          baseDetails.steps = [
            'Perform a full charge cycle (0-100%)',
            'Keep vehicle plugged in after reaching 100% for 2-3 hours',
            'BMS will automatically balance cells during this period',
            'Repeat if imbalance persists'
          ];
          baseDetails.safetyNotes = [
            'Normal process, no safety concerns',
            'Balancing is automatic via BMS'
          ];
          baseDetails.estimatedTime = '6-8 hours total (includes balancing time)';
        }
        break;

      case 'Thermal':
        baseDetails.impacts = [
          'Reduced battery performance',
          'Slower charging speeds',
          'Potential thermal runaway if temperature exceeds safe limits'
        ];
        baseDetails.steps = [
          'Park vehicle in shaded area if possible',
          'Avoid fast charging until temperature normalizes',
          'Check coolant levels in battery thermal management system',
          'Allow 30-60 mins cooling time before charging'
        ];
        baseDetails.safetyNotes = [
          '⚠️ Do not charge if battery temp > 45°C',
          '⚠️ If temp > 50°C, contact service center immediately'
        ];
        baseDetails.estimatedTime = '30-60 minutes for cooling';
        break;

      case 'Motor':
        baseDetails.impacts = [
          'Reduced motor efficiency',
          'Possible power deration to protect motor',
          'Increased energy consumption'
        ];
        baseDetails.steps = [
          'Reduce aggressive acceleration and high speeds',
          'Enable ECO driving mode',
          'Check coolant levels',
          'Inspect air vents for blockages'
        ];
        baseDetails.safetyNotes = [
          'Motor is designed to tolerate high temps',
          'Power deration is automatic protective measure'
        ];
        baseDetails.estimatedTime = '15-30 minutes for temperature to normalize';
        break;

      case 'Diagnostic':
        const dtcCode = rec.title.replace('DTC: ', '');
        baseDetails.overview = `Diagnostic Trouble Code ${dtcCode} has been detected.`;
        baseDetails.impacts = [
          'System monitoring and reporting the issue',
          'May affect vehicle performance or accuracy of readings',
          'Requires diagnostic scan for detailed analysis'
        ];
        baseDetails.steps = [
          'Note when the code first appeared',
          'Check if issue is intermittent or persistent',
          'Use OBD-II scanner for detailed code reading',
          'Contact authorized service center for professional diagnosis'
        ];
        baseDetails.safetyNotes = [
          'Severity depends on specific DTC code',
          'Refer to service manual for code-specific guidance'
        ];
        baseDetails.estimatedTime = 'Varies based on diagnosis';
        break;

      case 'Maintenance':
        baseDetails.impacts = [
          'Ensures optimal vehicle performance',
          'Prevents unexpected breakdowns',
          'Maintains warranty validity'
        ];
        baseDetails.steps = [
          'Schedule appointment with authorized service center',
          'Bring vehicle service history',
          'Typical checks: brakes, tires, suspension, cooling system, HV connectors',
          'Software updates may also be performed'
        ];
        baseDetails.safetyNotes = [
          'Regular maintenance is crucial for EV safety',
          'HV system checks should only be done by certified technicians'
        ];
        baseDetails.estimatedTime = '2-4 hours at service center';
        break;

      default:
        baseDetails.overview = rec.description;
        baseDetails.steps = ['Follow recommended action'];
    }

    return baseDetails;
  };

  return (
    <div className="diagnostic-panel">
      <div className="panel-header">
        <h3>Live Diagnostics</h3>
        <div className="update-indicator">
          <span className="indicator-dot pulse"></span>
          <span className="indicator-text">Real-time</span>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="metrics-grid">
        {/* Battery SOC */}
        <div
          className={`metric-card glass-panel ${focusComponent === 'battery' ? 'active' : ''}`}
          onClick={() => onComponentFocus?.('battery')}
        >
          <div className="metric-header">
            <Battery size={24} color={getStatusColor('battery')} />
            <span className="metric-title">Battery</span>
          </div>
          <div className="metric-content">
            <ProgressRing
              value={stats?.battery.soc.value || 0}
              color={stats?.getHealthColor(stats.battery.soc.value)}
            />
            <div className="metric-details">
              <div className="detail-row">
                <span className="detail-label">Range</span>
                <span className="detail-value">{stats?.battery.range.formatted}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Health</span>
                <span className="detail-value text-success">{stats?.battery.soh.formatted}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Temperature */}
        <div
          className={`metric-card glass-panel ${focusComponent === 'battery' ? 'active' : ''}`}
          onClick={() => onComponentFocus?.('battery')}
        >
          <div className="metric-header">
            <Thermometer size={24} color={getStatusColor('battery')} />
            <span className="metric-title">Temperature</span>
          </div>
          <div className="metric-content">
            <div className="large-value">
              <span className="value-number">{stats?.battery.temperature.pack?.toFixed(1) || 0}</span>
              <span className="value-unit">°C</span>
            </div>
            <MiniChart data={tempHistory} color="#66B4C4" />
            <div className="metric-range">
              <span>Min: {stats?.battery.temperature.min?.toFixed(1) || 0}°C</span>
              <span>Max: {stats?.battery.temperature.max?.toFixed(1) || 0}°C</span>
            </div>
          </div>
        </div>

        {/* Voltage */}
        <div
          className={`metric-card glass-panel ${focusComponent === 'battery' ? 'active' : ''}`}
          onClick={() => onComponentFocus?.('battery')}
        >
          <div className="metric-header">
            <Zap size={24} color={getStatusColor('battery')} />
            <span className="metric-title">Pack Voltage</span>
          </div>
          <div className="metric-content">
            <div className="large-value">
              <span className="value-number">{stats?.battery.voltage.value?.toFixed(1) || 0}</span>
              <span className="value-unit">V</span>
            </div>
            <div className="metric-details">
              <div className="detail-row">
                <span className="detail-label">Cells</span>
                <span className="detail-value">{stats?.battery.cells.count || 0}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Avg Cell</span>
                <span className="detail-value">{stats?.battery.cells.formattedAvg}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Imbalance</span>
                <span className="detail-value">{stats?.battery.cells.formattedImbalance}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Motor */}
        <div
          className={`metric-card glass-panel ${focusComponent === 'motor' ? 'active' : ''}`}
          onClick={() => onComponentFocus?.('motor')}
        >
          <div className="metric-header">
            <Gauge size={24} color={getStatusColor('motor')} />
            <span className="metric-title">Motor</span>
          </div>
          <div className="metric-content">
            <div className="large-value">
              <span className="value-number">{stats?.motor.temperature.value?.toFixed(1) || 0}</span>
              <span className="value-unit">°C</span>
            </div>
            <div className="metric-details">
              <div className="detail-row">
                <span className="detail-label">Status</span>
                <span className="detail-value text-success">{stats?.motor.status.formatted}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Efficiency</span>
                <span className="detail-value">{stats?.motor.efficiency.formatted}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">RPM</span>
                <span className="detail-value">{stats?.motor.rpm.value || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cell Voltage Heatmap */}
      {focusComponent === 'battery' && battery?.cells && (
        <div className="expanded-section fade-in">
          <CellHeatmap
            cells={battery.cells.voltages?.map((voltage, i) => ({
              voltage,
              temperature: battery.temperature?.cells?.[i] || 28
            })) || []}
          />
        </div>
      )}

      {/* Motor Details */}
      {focusComponent === 'motor' && (
        <div className="expanded-section glass-panel fade-in">
          <h4>
            <Gauge size={20} />
            Motor Performance
          </h4>
          <div className="performance-grid">
            <div className="performance-item">
              <span className="perf-label">Max Power</span>
              <span className="perf-value">{motor?.specs?.maxPower || 0} kW</span>
            </div>
            <div className="performance-item">
              <span className="perf-label">Max Torque</span>
              <span className="perf-value">{motor?.specs?.maxTorque || 0} Nm</span>
            </div>
            <div className="performance-item">
              <span className="perf-label">Max RPM</span>
              <span className="perf-value">{motor?.specs?.maxRpm?.toLocaleString() || 0}</span>
            </div>
            <div className="performance-item">
              <span className="perf-label">Cooling</span>
              <span className="perf-value text-success">{motor?.cooling?.status || 'normal'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations & Fixtures Section */}
      <div className="recommendations-section">
        <div className="section-header">
          <h4>
            <Wrench size={20} />
            Recommendations & Actions
          </h4>
          <span className="recommendations-count">{recommendations.length}</span>
        </div>

        <div className="recommendations-list">
          {recommendations.map((rec) => (
            <div key={rec.id} className={`recommendation-card recommendation-${rec.severity}`}>
              <div className="recommendation-icon">{rec.icon}</div>
              <div className="recommendation-content">
                <div className="recommendation-header">
                  <h5>{rec.title}</h5>
                  <span className="recommendation-category">{rec.category}</span>
                </div>
                <p className="recommendation-description">{rec.description}</p>
                <div className="recommendation-footer">
                  <button
                    className="recommendation-action-btn"
                    onClick={() => setSelectedRecommendation(rec)}
                  >
                    {rec.severity === 'success' ? <CheckCircle size={14} /> : <Wrench size={14} />}
                    <span>{rec.action}</span>
                  </button>
                  <span className={`severity-badge severity-${rec.severity}`}>
                    {rec.severity.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendation Details Popup */}
      {selectedRecommendation && (
        <div className="recommendation-details-overlay" onClick={() => setSelectedRecommendation(null)}>
          <div className="recommendation-details-modal glass-panel" onClick={(e) => e.stopPropagation()}>
            <div className="details-modal-header">
              <div className="details-modal-title">
                <span className="details-icon">{selectedRecommendation.icon}</span>
                <h3>{selectedRecommendation.title}</h3>
              </div>
              <button className="details-close-btn" onClick={() => setSelectedRecommendation(null)}>✕</button>
            </div>

            <div className="details-modal-content">
              {(() => {
                const details = getRecommendationDetails(selectedRecommendation);
                return (
                  <>
                    {/* Overview */}
                    <div className="details-section">
                      <h4>Overview</h4>
                      <p>{details.overview}</p>
                    </div>

                    {/* Impacts */}
                    {details.impacts.length > 0 && (
                      <div className="details-section">
                        <h4>Potential Impacts</h4>
                        <ul className="details-list">
                          {details.impacts.map((impact, idx) => (
                            <li key={idx}>{impact}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Action Steps */}
                    {details.steps.length > 0 && (
                      <div className="details-section">
                        <h4>Recommended Steps</h4>
                        <ol className="details-steps">
                          {details.steps.map((step, idx) => (
                            <li key={idx}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* Safety Notes */}
                    {details.safetyNotes.length > 0 && (
                      <div className="details-section details-safety">
                        <h4>⚠️ Safety Notes</h4>
                        <ul className="details-safety-list">
                          {details.safetyNotes.map((note, idx) => (
                            <li key={idx}>{note}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Estimated Time */}
                    {details.estimatedTime && (
                      <div className="details-section details-time">
                        <h4>⏱️ Estimated Time</h4>
                        <p>{details.estimatedTime}</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="details-modal-footer">
              <button className="details-action-btn" onClick={() => setSelectedRecommendation(null)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiagnosticPanel;
